import { useState, useRef } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { Image, Icon } from "semantic-ui-react";
import { setGoogleAnalyticsEvent } from "src/common/helpers";
import { redirectToLogout } from "src/redux/actions/application";
import { useHistory } from "react-router";
import ImpersonationForm from "./ImpersonationForm";
import { handleSSORedirect } from "src/services/ssoRedirect";
import { UserI } from "src/types/User";
import { SSOLinkI } from "src/types/SSOLink";
import {
  Box,
  Flex,
  LightMode,
  ListItem,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  UnorderedList,
} from "@chakra-ui/react";
import LightDarkIcon from "src/icons/LightDarkIcon";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  GlobeIcon,
  IndividualIcon,
  LogoutIcon,
  TickIcon,
  TripReturnIcon,
} from "ctm-compass-react";
import PrimaryColorButton from "src/components/PrimaryColorButton";
import { useTranslate } from "@tolgee/react";
import { useThemePreferenceSync } from "src/common/hooks/themePreference";
import { SUPPORTED_LANGUAGES, SupportedLanguage } from "src/i18n/languages";
import { RootState } from "src/redux";
import { setLanguage } from "src/redux/actions/user";
import FocusLock from "react-focus-lock";

/** User Profile for header with dropdown menu */

const renderAvatar = (user: UserI) => {
  if (
    user.givenName &&
    user.givenName.length &&
    user.familyName &&
    user.familyName.length
  ) {
    return (
      <span className="avatar">
        <span>{`${user.givenName[0]}${user.familyName[0]}`}</span>
      </span>
    );
  }

  if (user.picture) {
    return (
      <Image
        src={user.picture}
        avatar
        className="avatar"
        style={{ height: "40px", width: "40px" }}
      />
    );
  }
};

interface ProfileI {
  colors: any;
  user: UserI;
  globalSsoLinks?: SSOLinkI[];
  initializeImpersonation: (params: {
    email: string;
    id: string;
    token: string;
  }) => void;
  clearImpersonation: () => void;
  initializeSwitchAccount: (params: {
    companyName: string;
    companyID: string;
  }) => void;
  clearSwitchAccount: () => void;
  isImpersonating: string;
}

const Profile = ({
  colors,
  user,
  globalSsoLinks,
  initializeImpersonation,
  clearImpersonation,
  initializeSwitchAccount,
  clearSwitchAccount,
  isImpersonating,
}: ProfileI) => {
  const [isProfileOpen, setProfileOpen] = useState(false); // only for rendering chevron icon, we are not controlling the portal
  const [isEditingLightMode, setEditingLightMode] = useState(false);
  const [isEditingLanguage, setEditingLanguage] = useState(false);
  const [isSelectAccountMenu, setInSelectAccountMenu] = useState(false);
  const [isInImpersonationMenu, setInImpersonationMenu] = useState(false);

  const history = useHistory();
  const triggerRef = useRef(null);
  const { colorMode, applyThemePreference } = useThemePreferenceSync(colors);

  function resetPopoverState() {
    // If we reset popover state right on the onClose event, you can see the UI change
    // Delay it a bit to avoid the weird flicker
    setTimeout(() => {
      setEditingLightMode(false);
      setEditingLanguage(false);
      setInSelectAccountMenu(false);
      setInImpersonationMenu(false);
    }, 100);
  }

  const handleToggle = (val) => {
    setGoogleAnalyticsEvent({
      action: "portal_dashboard_theme_switch",
      category: val ? "breezy" : "galaxy",
    });
    applyThemePreference(val);
  };

  const renderTrigger = () => {
    const currUser = (user || {}) as UserI;
    if (!currUser.givenName) {
      return (
        <button
          className="unstyled-button"
          data-aut="UserProfile"
          ref={triggerRef}
        >
          <Icon name="caret down" />
        </button>
      );
    }
    const name = currUser.familyName
      ? `${currUser.givenName} ${currUser.familyName}`
      : currUser.givenName;

    return (
      <button
        className="unstyled-button"
        id="user-profile"
        data-aut="UserProfile"
        ref={triggerRef}
      >
        {renderAvatar(currUser)}
        <span className="greeting">{name}</span>
        <Box as="span" ml={2}>
          {isProfileOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </Box>
      </button>
    );
  };

  function renderMenuContent() {
    if (isEditingLightMode)
      return (
        <LightModeMenu
          colorMode={colorMode}
          handleToggle={handleToggle}
          setEditingLightMode={setEditingLightMode}
        />
      );
    if (isEditingLanguage)
      return <LanguageMenu setEditingLanguage={setEditingLanguage} />;
    if (isSelectAccountMenu)
      return (
        <SwitchAccountsMenu
          globalSsoLinks={globalSsoLinks}
          setInSelectAccountMenu={setInSelectAccountMenu}
          externalAccounts={user.switchAccount}
          isImpersonating={isImpersonating}
          clearSwitchAccount={clearSwitchAccount}
          initializeSwitchAccount={initializeSwitchAccount}
        />
      );
    if (isInImpersonationMenu)
      return (
        <ImpersonationMenu
          clearImpersonation={clearImpersonation}
          initializeImpersonation={initializeImpersonation}
          isImpersonating={isImpersonating}
          setInImpersonationMenu={setInImpersonationMenu}
        />
      );
    return (
      <DefaultMenu
        globalSsoLinks={globalSsoLinks}
        history={history}
        isImpersonating={isImpersonating}
        setEditingLightMode={setEditingLightMode}
        setEditingLanguage={setEditingLanguage}
        setInSelectAccountMenu={setInSelectAccountMenu}
        setInImpersonationMenu={setInImpersonationMenu}
        user={user}
      />
    );
  }

  return (
    <Popover
      onOpen={() => setProfileOpen(true)}
      onClose={() => {
        setProfileOpen(false);
        resetPopoverState();
      }}
      isLazy
    >
      <PopoverTrigger>{renderTrigger()}</PopoverTrigger>
      <Portal>
        <PopoverContent>
          <FocusLock>
            <PopoverBody p={0} color="var(--defaultGray)" borderRadius={4}>
              {renderMenuContent()}
            </PopoverBody>
          </FocusLock>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

function DefaultMenu({
  globalSsoLinks,
  history,
  isImpersonating,
  setEditingLightMode,
  setEditingLanguage,
  setInSelectAccountMenu,
  setInImpersonationMenu,
  user,
}) {
  const { t } = useTranslate();
  return (
    <UnorderedList p={0} m={0} mr={0} className="profile-menu-list">
      <ListItem onClick={() => setEditingLightMode(true)}>
        <button className="unstyled-button profile-menu-button">
          <Flex justify={"space-between"} alignItems={"center"}>
            <Flex>
              <LightDarkIcon
                fill="#ABB1BB"
                className="profile-menu-left-icon"
              />{" "}
              {t({ key: "profile.theme.title", defaultValue: "Color Theme" })}
            </Flex>
            <ChevronRightIcon color="var(--subtleGray)" />
          </Flex>
        </button>
      </ListItem>
      <ListItem>
        <button
          className="unstyled-button profile-menu-button"
          onClick={() => setEditingLanguage(true)}
        >
          <Flex justify={"space-between"} alignItems={"center"}>
            <Flex alignItems={"center"}>
              <GlobeIcon color="#ABB1BB" className="profile-menu-left-icon" />
              {t({
                key: "profile.language.title",
                defaultValue: "Language",
              })}
            </Flex>
            <ChevronRightIcon color="var(--subtleGray)" />
          </Flex>
        </button>
      </ListItem>
      {globalSsoLinks?.length || user.switchAccount?.length ? (
        <ListItem>
          <button
            className="unstyled-button profile-menu-button"
            onClick={() => setInSelectAccountMenu(true)}
          >
            <Flex justify={"space-between"} alignItems={"center"}>
              <Flex alignItems={"center"}>
                <TripReturnIcon
                  color="#ABB1BB"
                  className="profile-menu-left-icon"
                />
                {t({
                  key: "profile.switchAccounts.title",
                  defaultValue: "Switch Accounts",
                })}
              </Flex>
              <ChevronRightIcon color="var(--subtleGray)" />
            </Flex>
          </button>
        </ListItem>
      ) : null}
      {Boolean(user.canImpersonate || isImpersonating) && (
        <ListItem>
          <button
            className="unstyled-button profile-menu-button"
            onClick={() => setInImpersonationMenu(true)}
          >
            <Flex justify={"space-between"} alignItems={"center"}>
              <Flex alignItems={"center"}>
                <IndividualIcon
                  color="#ABB1BB"
                  className="profile-menu-left-icon"
                />{" "}
                {t({
                  key: "profile.impersonate.title",
                  defaultValue: "Impersonate User",
                })}
              </Flex>
              <ChevronRightIcon color="var(--subtleGray)" />
            </Flex>
          </button>
        </ListItem>
      )}
      <ListItem>
        <button
          className="unstyled-button profile-menu-button"
          onClick={() => history.push("/logout")}
        >
          <Flex alignItems={"center"}>
            <LogoutIcon color="#ABB1BB" className="profile-menu-left-icon" />
            {t({ key: "profile.logout", defaultValue: "Logout" })}
          </Flex>
        </button>
      </ListItem>
    </UnorderedList>
  );
}

function LanguageMenu({ setEditingLanguage }) {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const language = useSelector((state: RootState) => state.user.language);

  const handleLanguageChange = (newLanguage: SupportedLanguage) => {
    setGoogleAnalyticsEvent({
      action: "portal_dashboard_language_switch",
      category: newLanguage,
    });
    dispatch(setLanguage(newLanguage));
  };

  return (
    <UnorderedList
      p={0}
      m={0}
      mr={0}
      className="profile-menu-list language-menu-list"
    >
      <ListItem color="var(--defaultGray)">
        <button
          className="unstyled-button profile-menu-button"
          onClick={() => setEditingLanguage(false)}
        >
          <Flex justify={"space-between"} alignItems={"center"}>
            <ChevronLeftIcon
              className="profile-menu-left-icon"
              color="var(--subtleGray)"
            />
            <span>
              {t({
                key: "profile.language.switch",
                defaultValue: "Select Language",
              })}
            </span>
            <span />
          </Flex>
        </button>
      </ListItem>
      {SUPPORTED_LANGUAGES.map(({ tag, label }) => {
        const isSelected = language === tag;
        return (
          <ListItem color="var(--defaultGray)" key={tag}>
            <button
              aria-pressed={isSelected}
              className={`unstyled-button profile-menu-button${
                isSelected ? " checked" : ""
              }`}
              lang={tag}
              onClick={() => handleLanguageChange(tag)}
            >
              <Flex justify={"space-between"} alignItems={"center"}>
                <span>{label}</span>
                {isSelected ? (
                  <span aria-hidden="true">
                    <TickIcon />
                  </span>
                ) : (
                  <span />
                )}
              </Flex>
            </button>
          </ListItem>
        );
      })}
    </UnorderedList>
  );
}

function LightModeMenu({ colorMode, handleToggle, setEditingLightMode }) {
  const { t } = useTranslate();

  return (
    <UnorderedList p={0} m={0} mr={0} className="profile-menu-list">
      <ListItem color="var(--defaultGray)">
        <button
          className="unstyled-button profile-menu-button"
          onClick={() => setEditingLightMode(false)}
        >
          <Flex justify={"space-between"} alignItems={"center"}>
            <ChevronLeftIcon
              className="profile-menu-left-icon"
              color="var(--subtleGray)"
            />
            <span>
              {" "}
              {t({
                key: "profile.theme.switch",
                defaultValue: "Switch Themes",
              })}
            </span>
            <span />
          </Flex>
        </button>
      </ListItem>
      <ListItem color="var(--defaultGray)">
        <button
          className={
            colorMode === "light"
              ? "unstyled-button profile-menu-button checked"
              : "unstyled-button profile-menu-button"
          }
          onClick={() => handleToggle(true)}
        >
          <Flex justify={"space-between"} alignItems={"center"}>
            <span>
              {t({
                key: "profile.theme.light",
                defaultValue: "Breezy (light)",
              })}
            </span>
            {colorMode === "light" ? <TickIcon /> : <span />}
          </Flex>
        </button>
      </ListItem>
      <ListItem color="var(--defaultGray)">
        <button
          className={
            colorMode === "light"
              ? "unstyled-button profile-menu-button"
              : "unstyled-button profile-menu-button checked"
          }
          onClick={() => handleToggle(false)}
        >
          <Flex justify={"space-between"} alignItems={"center"}>
            <span>
              {t({ key: "profile.theme.dark", defaultValue: "Galaxy (dark)" })}
            </span>
            {colorMode === "light" ? <span /> : <TickIcon />}
          </Flex>
        </button>
      </ListItem>
    </UnorderedList>
  );
}

function ImpersonationMenu({
  clearImpersonation,
  initializeImpersonation,
  isImpersonating,
  setInImpersonationMenu,
}) {
  const { t } = useTranslate();

  return (
    <UnorderedList p={0} m={0} mr={0} className="profile-menu-list">
      <ListItem color="var(--defaultGray)">
        <button
          className="unstyled-button profile-menu-button"
          onClick={() => setInImpersonationMenu(false)}
        >
          <Flex justify={"space-between"} alignItems={"center"}>
            <ChevronLeftIcon
              className="profile-menu-left-icon"
              color="var(--subtleGray)"
            />
            <span>
              {t({
                key: "profile.impersonate.title",
                defaultValue: "Impersonate User",
              })}
            </span>
            <span />
          </Flex>
        </button>
      </ListItem>
      <ListItem color="var(--defaultGray)" padding="10px 16px">
        <ImpersonationForm
          clearImpersonation={clearImpersonation}
          isImpersonating={isImpersonating}
          initializeImpersonation={initializeImpersonation}
        />
      </ListItem>
    </UnorderedList>
  );
}

function SwitchAccountsMenu({
  externalAccounts,
  globalSsoLinks,
  initializeSwitchAccount,
  clearSwitchAccount,
  isImpersonating,
  setInSelectAccountMenu,
}) {
  const { t } = useTranslate();

  const externalAccount = useSelector(
    (state: any) => state?.externalAccount?.account,
  );
  return (
    <UnorderedList p={0} m={0} mr={0} className="profile-menu-list">
      <ListItem color="var(--defaultGray)">
        <button
          className="unstyled-button profile-menu-button"
          onClick={() => setInSelectAccountMenu(false)}
        >
          <Flex justify={"space-between"} alignItems={"center"}>
            <ChevronLeftIcon
              className="profile-menu-left-icon"
              color="var(--subtleGray)"
            />
            <span>
              {t({
                key: "profile.switchAccounts.title",
                defaultValue: "Switch Accounts",
              })}
            </span>
            <span />
          </Flex>
        </button>
      </ListItem>
      {externalAccount ? (
        <ListItem color="var(--defaultGray)" padding="10px 16px">
          <p>
            {t({
              key: "profile.switchAccounts.switched-to",
              params: { companyName: externalAccount.companyName },
              defaultValue: `Switched to: ${externalAccount.companyName}`,
            })}
          </p>
          <LightMode>
            <PrimaryColorButton
              onClick={clearSwitchAccount}
              colorModeOverride="light"
            >
              {t({
                key: "profile.switchAccounts.switch-back",
                defaultValue: "Switch Back",
              })}
            </PrimaryColorButton>
          </LightMode>
        </ListItem>
      ) : (
        <>
          {globalSsoLinks?.length
            ? globalSsoLinks.map((link) => {
                const url = link.isDirectLink ? link.href : undefined;
                return (
                  <ListItem key={link.id}>
                    <a
                      className="profile-menu-button"
                      href={url}
                      target={link.openInNewWindow ? "_blank" : undefined}
                      rel="noreferrer"
                      data-aut={`Profile|SSOLink|${link.label}`}
                      tabIndex={0}
                      onClick={() => {
                        if (!link.isDirectLink)
                          handleSSORedirect(link, history, isImpersonating);
                        setGoogleAnalyticsEvent({
                          action: "portal_dashboard_global_sso",
                          category: link.identifier,
                        });
                      }}
                    >
                      {link.label}
                    </a>
                  </ListItem>
                );
              })
            : null}
          {externalAccounts?.length
            ? externalAccounts.map((account, i) => {
                return (
                  <ListItem key={i}>
                    <button
                      className="profile-menu-button unstyled-button"
                      tabIndex={0}
                      onClick={() => initializeSwitchAccount(account)}
                    >
                      {account.companyName}
                    </button>
                  </ListItem>
                );
              })
            : null}
        </>
      )}
    </UnorderedList>
  );
}

const mapStateToProps = (state) => {
  return {
    user: state.user.data,
    loading: state.user.loading,
    globalSsoLinks: state.globalSsoLinks,
    isImpersonating: state.impersonation.isImpersonating,
    colors: state.application.colors,
  };
};

const mapDispatchToProp = (dispatch) => ({
  redirectToLogout: () => dispatch(redirectToLogout()),
});

export default connect(mapStateToProps, mapDispatchToProp)(Profile);
